import { createTheme, ThemeOptions } from '@mui/material';
import { convertBreakpoint } from '../utils/usefulFunctions';
import { ThemeTypes } from '../types/theme/themeTypes';
import { getSystemTheme } from '../utils/userProfileContextHelpers';
import {
    getCSSVariable,
    getThemeVariable,
} from '../stylesheets/cssVariablesUtils';

const lightTheme = {
    primary: {
        light: getThemeVariable('lightPrimary200'),
        main: getThemeVariable('lightPrimary400'),
        dark: getThemeVariable('lightPrimary700'),
    },
    secondary: {
        light: getThemeVariable('lightGrey200'),
        main: getThemeVariable('lightGrey600'),
        dark: getThemeVariable('lightGrey700'),
    },
    error: {
        light: getThemeVariable('lightError200'),
        main: getThemeVariable('lightError400'),
        dark: getThemeVariable('lightError700'),
    },
    background: {
        default: getThemeVariable('lightBackground'),
        paper: getThemeVariable('lightWhite'),
    },
};

const darkTheme = {
    primary: {
        light: getThemeVariable('darkPrimary200'),
        main: getThemeVariable('darkPrimary400'),
        dark: getThemeVariable('darkPrimary700'),
    },
    secondary: {
        light: getThemeVariable('darkGrey200'),
        main: getThemeVariable('darkGrey600'),
        dark: getThemeVariable('darkGrey700'),
    },
    error: {
        light: getThemeVariable('darkError200'),
        main: getThemeVariable('darkError400'),
        dark: getThemeVariable('darkError700'),
    },
    background: {
        default: getThemeVariable('darkBackground'),
        paper: getThemeVariable('darkWhite'),
    },
};

const defaultThemeOptions: ThemeOptions = {
    breakpoints: {
        values: {
            xs: 0,
            sm: convertBreakpoint(getCSSVariable('sm')),
            md: convertBreakpoint(getCSSVariable('md')),
            lg: convertBreakpoint(getCSSVariable('lg')),
            xl: convertBreakpoint(getCSSVariable('xl')),
            // xxl: convertBreakpoint(styles.xxl),
            // xxxl: convertBreakpoint(styles.xxxl),
        },
    },
    typography: {
        fontFamily: `'Montserrat', sans-serif`,
        h1: { fontSize: 24, fontWeight: 600 },
        h2: { fontSize: 20, fontWeight: 600 },
        h3: { fontSize: 14, fontWeight: 600 },
        button: {
            fontFamily: `'Montserrat', sans-serif`,
            fontWeight: 600,
            textTransform: 'none',
            lineHeight: 1.5,
        },
        body1: {
            fontSize: 14,
            fontFamily: `'Montserrat', sans-serif`,
            color: 'var(--text)',
        },
        body2: {
            fontSize: 14,
            fontWeight: 600,
            fontFamily: `'Montserrat', sans-serif`,
        },
    },
    components: {
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: 'var(--grey-700)',
                    '&:focus': { outline: 0 },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    '&::first-letter': { textTransform: 'uppercase' },
                    '&:focus': { outline: 0 },
                },
                text: {
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02) !important',
                        color: getCSSVariable('primary700'),
                    },
                },
                contained: {
                    '&:hover': { color: getCSSVariable('white') },
                    // '&.Mui-disabled': {
                    //     backgroundColor: getCSSVariable('disabledButton'),
                    // },
                },
                outlined: {
                    borderWidth: '1px',
                    borderColor: 'var(--border)',
                    '&:hover': { borderWidth: '1px' },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }) => ({
                    background: 'var(--background)',
                    '& input[type="time"]::-webkit-calendar-picker-indicator': {
                        filter:
                            theme.palette.mode === ThemeTypes.DARK
                                ? 'invert(1)'
                                : 'none',
                        cursor: 'pointer',
                    },
                }),
                input: {
                    color: 'var(--text)',
                    caretColor: 'var(--text)',
                    '&:-webkit-autofill': {
                        WebkitTextFillColor: 'var(--text)',
                        WebkitBoxShadow: '0 0 0 100px var(--background) inset',
                        caretColor: 'var(--text)',
                    },
                },
                notchedOutline: {
                    border: '1px solid var(--border)',
                    borderRadius: 5,
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    backgroundColor: 'var(--background) !important',
                    padding: '0 5px',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    '&.MuiPickersPopper-paper': {
                        backgroundImage: 'none',
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    position: 'relative',
                    fontSize: 10,
                    height: 16,
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                listbox: {
                    border: '1px solid var(--border)',
                    background: 'var(--white)',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    overflow: 'unset',
                    borderRadius: 5,
                    boxShadow: 'var(--card-shadow)',
                    transition: 'inset',
                    border: '1px solid var(--border)',
                    background: 'var(--card-background)',
                    color: 'var(--text)',
                    '&:hover': { boxShadow: 'var(--card-shadow)' },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontFamily: `'Montserrat', sans-serif !important`,
                    fontWeight: 400,
                    borderBottom: '1px solid var(--border)',
                    '&.MuiTableCell-head': {
                        fontWeight: 600,
                        backgroundColor: 'var(--white)',
                        color: 'var(--text)',
                        borderBottom: '1px solid var(--border)',
                    },
                },
                stickyHeader: {
                    fontFamily: `'Montserrat', sans-serif !important`,
                    background: 'unset',
                    fontWeight: 500,
                    backgroundColor: 'var(--white)',
                    color: 'var(--text) !important',
                },
                head: {
                    fontWeight: 500,
                    backgroundColor: 'var(--white)',
                    color: 'var(--text) !important',
                    borderBottom: '1px solid var(--border)',
                },
                body: {
                    borderBottom: '1px solid var(--border)',
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    height: 7,
                    borderRadius: 5,
                    backgroundColor: 'var(--background)',
                },
                bar: {
                    borderRadius: 5,
                    backgroundColor: 'var(--always-green)',
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                label: { '&.MuiStepLabel-alternativeLabel': { marginTop: 8 } },
            },
        },
        MuiStepConnector: { styleOverrides: { root: { top: 6 } } },
        MuiAlert: {
            styleOverrides: {
                standardInfo: {
                    backgroundColor: 'var(--background)',
                    color: 'var(--secondary-700)',
                    '& .MuiAlert-icon': { color: 'var(--secondary-700)' },
                },
                standardWarning: {
                    backgroundColor: 'var(--warning-alert-background)',
                },
            },
        },
        MuiAlertTitle: {
            styleOverrides: {
                root: { color: 'var(--secondary-700)' },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    background: 'var(--card-background)',
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    background: 'var(--card-background)',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: 'var(--white)',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: getThemeVariable('darkWhite'),
                    backgroundImage: `linear-gradient(
                            rgba(255, 255, 255, 0.12),
                            rgba(255, 255, 255, 0.12)
                        )`,
                    boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: { background: 'var(--card-background)' },
            },
        },
    },
};

const systemTheme = getSystemTheme();
const modeThemes = {
    [ThemeTypes.LIGHT]: lightTheme,
    [ThemeTypes.DARK]: darkTheme,
    [ThemeTypes.SYSTEM]:
        systemTheme === ThemeTypes.DARK ? darkTheme : lightTheme,
};

export const getTheme = (mode: ThemeTypes) => {
    const modeTheme = modeThemes[mode];
    const theme = {
        ...defaultThemeOptions,
        palette: {
            mode: mode === ThemeTypes.SYSTEM ? systemTheme : mode,
            ...modeTheme,
        },
    };
    return createTheme(theme);
};
